/**
 * GameAnalyticsIntegration - Clean separation between game logic and analytics
 * Handles all analytics tracking for game events with proper business logic
 */

import { AnalyticsService } from './AnalyticsService';
import { GameMode, GameState, DailyState } from '../types/GameTypes';
import { GAME_CONFIG } from '../config';
import { ANALYTICS_EVENTS, SharingPlatform, ModalType } from '../types/AnalyticsConstants';

export class GameAnalyticsIntegration {
  private analyticsService: AnalyticsService;
  private gameStartTimes: Map<string, number> = new Map();

  constructor(analyticsService?: AnalyticsService) {
    this.analyticsService = analyticsService || AnalyticsService.getInstance();
  }

  /**
   * Track when a game starts
   */
  trackGameStart(gameMode: GameMode, wordLength: number, secretWord: string): void {
    try {
      // Store game start time for duration tracking
      const gameKey = `${gameMode}_${secretWord}`;
      this.gameStartTimes.set(gameKey, Date.now());
      
      // Track game start event
      this.analyticsService.trackGameEvent({
        event_name: ANALYTICS_EVENTS.GAME.START,
        timestamp: Date.now(),
        game_mode: gameMode,
        word_length: wordLength
      });
    } catch (error) {
      console.error('Analytics: Failed to track game start', error);
    }
  }

  /**
   * Track when a daily challenge starts (first word of the day)
   */
  trackDailyChallengeStart(dailyState: DailyState | null): void {
    if (dailyState && dailyState.results.length === 0) {
      // This is the start of a new daily challenge
      this.analyticsService.trackEvent({
        event_name: ANALYTICS_EVENTS.GAME.DAILY_START,
        timestamp: Date.now(),
        date: new Date().toISOString().split('T')[0],
        time: new Date().toISOString().split('T')[1].split('.')[0]
      });
    }
  }

  /**
   * Track when any game completes (both daily and free-play)
   */
  trackGameComplete(gameState: GameState, gameMode: GameMode): void {
    // Calculate game duration
    const gameKey = `${gameMode}_${gameState.secretWord}`;
    const startTime = this.gameStartTimes.get(gameKey) || Date.now();
    const duration = Date.now() - startTime;
    this.gameStartTimes.delete(gameKey); // Clean up
    
    // Track game completion event
    this.analyticsService.trackGameEvent({
      event_name: ANALYTICS_EVENTS.GAME.COMPLETE,
      timestamp: Date.now(),
      success: gameState.gameWon,
      score: gameState.score,
      attempts: gameState.guessedLetters.length,
      time_duration: duration,
      perfect_game: this.isPerfectGame(gameState)
    });

    // Track first game completion milestone
    if (gameState.gameWon && this.isFirstGameCompletion()) {
      this.trackFirstGameComplete(duration);
    }
  }

  /**
   * Track when daily challenge completes (all words 5-10 finished)
   */
  trackDailyChallengeComplete(totalScore: number): void {
    this.analyticsService.trackEvent({
      event_name: ANALYTICS_EVENTS.GAME.DAILY_COMPLETE,
      timestamp: Date.now(),
      final_score: totalScore,
      max_possible_score: this.calculateMaxPossibleDailyScore(),
      date: new Date().toISOString().split('T')[0],
      time: new Date().toISOString().split('T')[1].split('.')[0]
    });
  }

  /**
   * Calculate maximum possible score for daily challenge
   * Based on actual game rules: score = letterGuessesLeft when you win
   * Daily challenge: one word each of lengths 5, 6, 7, 8, 9, 10
   * Max score per word = GAME_CONFIG.maxLetterGuesses (6)
   */
  private calculateMaxPossibleDailyScore(): number {
    // Daily challenge has 6 words (lengths 5-10), each can score max 6 points
    const wordsInDaily = GAME_CONFIG.maxWordLength - GAME_CONFIG.minWordLength + 1; // 10 - 5 + 1 = 6
    const maxScorePerWord = GAME_CONFIG.maxLetterGuesses; // 6
    return wordsInDaily * maxScorePerWord; // 6 * 6 = 36
  }

  /**
   * Determine if a game was perfect (solved with maximum guesses remaining)
   */
  private isPerfectGame(gameState: GameState): boolean {
    if (!gameState.gameWon) return false;
    
    // Perfect game means:
    // 1. Game was won
    // 2. Used minimum possible guesses (all letters revealed with minimal guessing)
    // 3. Score equals maximum letter guesses
    return gameState.score === GAME_CONFIG.maxLetterGuesses;
  }

  /**
   * Track when user settings change
   */
  trackSettingsChange(settingName: string, oldValue: string, newValue: string): void {
    try {
      // Determine user type based on session count
      const userType = this.getUserType();
      
      this.analyticsService.trackUserBehavior({
        event_name: ANALYTICS_EVENTS.USER.SETTINGS_CHANGE,
        timestamp: Date.now(),
        setting_name: settingName,
        old_value: oldValue,
        new_value: newValue,
        user_type: userType
      });
    } catch (error) {
      console.error('Analytics: Failed to track settings change', error);
    }
  }

  /**
   * Track when user switches between game modes
   */
  trackModeSwitch(fromMode: GameMode, toMode: GameMode, triggerSource: 'button' | 'navigation'): void {
    this.analyticsService.trackUserBehavior({
      event_name: ANALYTICS_EVENTS.USER.MODE_SWITCH,
      timestamp: Date.now(),
      from_mode: fromMode,
      to_mode: toMode,
      trigger_source: triggerSource
    });
  }

  /**
   * Track performance issues
   */
  trackPerformanceIssue(metricName: 'LCP' | 'FID' | 'CLS' | 'custom', value: number, thresholdExceeded: boolean): void {
    this.analyticsService.trackPerformance({
      event_name: ANALYTICS_EVENTS.SYSTEM.PERFORMANCE_ISSUE,
      timestamp: Date.now(),
      metric_name: metricName,
      value: value,
      threshold_exceeded: thresholdExceeded
    });
  }

  /**
   * Track user engagement metrics
   */
  trackUserEngagement(sessionDuration: number, gamesPerSession: number, returnVisitor: boolean): void {
    this.analyticsService.trackUserBehavior({
      event_name: ANALYTICS_EVENTS.USER.ENGAGEMENT,
      timestamp: Date.now(),
      session_duration: sessionDuration,
      games_per_session: gamesPerSession,
      return_visitor: returnVisitor
    });
  }

  /**
   * Track return visits
   */
  trackReturnVisit(daysSinceLastVisit: number, sessionCount: number, totalGamesPlayed: number): void {
    this.analyticsService.trackEvent({
      event_name: ANALYTICS_EVENTS.USER.RETURN_VISIT,
      timestamp: Date.now(),
      days_since_last_visit: daysSinceLastVisit,
      session_count: sessionCount,
      total_games_played: totalGamesPlayed
    });
  }

  /**
   * Track first game completion milestone
   */
  trackFirstGameComplete(timeToFirstCompletion: number): void {
    this.analyticsService.trackConversion({
      event_name: ANALYTICS_EVENTS.GAME.FIRST_COMPLETE,
      timestamp: Date.now(),
      time_to_first_completion: timeToFirstCompletion
    });
  }

  /**
   * Track streak milestones
   */
  trackStreakMilestone(streakLength: number): void {
    this.analyticsService.trackConversion({
      event_name: ANALYTICS_EVENTS.CONVERSION.STREAK_MILESTONE,
      timestamp: Date.now(),
      streak_length: streakLength
    });
  }

  /**
   * Track errors encountered by users
   */
  trackError(errorType: string, errorContext: string, userActionTaken?: string): void {
    this.analyticsService.trackEvent({
      event_name: ANALYTICS_EVENTS.SYSTEM.ERROR_ENCOUNTERED,
      timestamp: Date.now(),
      error_type: errorType,
      error_context: errorContext,
      user_action_taken: userActionTaken
    });
  }

  /**
   * Track sharing events - critical for NPS measurement
   */
  trackShare(platform: SharingPlatform, dailyScore: number, streakLength: number, wasSuccessful: boolean = true): void {
    try {
      const eventName = this.getShareEventName(platform);
      
      this.analyticsService.trackUserBehavior({
        event_name: eventName as 'share_clipboard' | 'share_twitter' | 'share_facebook' | 'share_whatsapp',
        platform: platform,
        daily_score: dailyScore,
        streak_length: streakLength,
        successful: wasSuccessful,
        user_type: this.getUserType()
      });
    } catch (error) {
      console.error('Analytics: Failed to track share event', error);
    }
  }

  /**
   * Track modal opening events - useful for understanding user engagement with help/settings
   */
  trackModalOpen(modalType: ModalType): void {
    try {
      this.analyticsService.trackUserBehavior({
        event_name: ANALYTICS_EVENTS.USER.MODAL_OPEN,
        modal_type: modalType as 'settings' | 'how_to_play' | 'results',
        user_type: this.getUserType()
      });
    } catch (error) {
      console.error('Analytics: Failed to track modal open', error);
    }
  }

  /**
   * Get the appropriate event name for sharing platform
   */
  private getShareEventName(platform: SharingPlatform): 'share_clipboard' | 'share_twitter' | 'share_facebook' | 'share_whatsapp' {
    switch (platform) {
      case 'clipboard':
        return ANALYTICS_EVENTS.SHARING.CLIPBOARD;
      case 'twitter':
        return ANALYTICS_EVENTS.SHARING.TWITTER;
      case 'facebook':
        return ANALYTICS_EVENTS.SHARING.FACEBOOK;
      case 'whatsapp':
        return ANALYTICS_EVENTS.SHARING.WHATSAPP;
      default:
        return ANALYTICS_EVENTS.SHARING.CLIPBOARD;
    }
  }

  /**
   * Check if this is the user's first game completion
   */
  private isFirstGameCompletion(): boolean {
    try {
      const hasCompletedBefore = localStorage.getItem('has_completed_game');
      if (!hasCompletedBefore) {
        localStorage.setItem('has_completed_game', 'true');
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  /**
   * Get user type based on activity
   */
  private getUserType(): 'new' | 'returning' {
    try {
      const sessionCount = localStorage.getItem('analytics_session_count');
      return sessionCount && parseInt(sessionCount, 10) > 1 ? 'returning' : 'new';
    } catch {
      return 'new';
    }
  }

  /**
   * Clean up any remaining game start times (for memory management)
   */
  cleanup(): void {
    this.gameStartTimes.clear();
  }
}